import { useState } from "react"
import { FaCircleCheck, FaFile } from "react-icons/fa6"
import { FiInfo } from "react-icons/fi"
import { useNavigate } from "react-router-dom"
import { Box, Center, HStack, Tooltip, VStack } from "@chakra-ui/react"
import { Button, Text, Toaster, toaster, UploadModal } from "@/components"
import { useViewer } from "@/contexts"
import { useClientDeals, useUploadDocuments } from "@/hooks"

const InfoTooltip = ({ children }: { children: React.ReactNode }) => (
  <Tooltip.Root openDelay={0} closeDelay={100}>
    <Tooltip.Trigger asChild>
      <Box as="span" cursor="pointer" display="inline-flex" alignItems="center" ml={1}>
        <FiInfo size={14} color="#718096" />
      </Box>
    </Tooltip.Trigger>
    <Tooltip.Positioner>
      <Tooltip.Content bg="gray.700" color="white" px={3} py={2} borderRadius="md" maxW="350px">
        <Tooltip.Arrow>
          <Tooltip.ArrowTip />
        </Tooltip.Arrow>
        {children}
      </Tooltip.Content>
    </Tooltip.Positioner>
  </Tooltip.Root>
)

type OnboardingStep = "check-email" | "upload-equity" | "upload-fmv" | "thank-you"

const DOC_TYPE_TO_PREFIX: Record<string, string> = {
  "Equity Holdings (ISO, NSO, Shares, RSUs)": "Equity Holdings",
  "FMV (409A) Confirmation": "FMV Confirmation",
}

const WHITE_LISTED_FILE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword"
]

export const OnboardingComponent = () => {
  const navigate = useNavigate()
  const { viewedClient } = useViewer()
  const { data: deals } = useClientDeals()
  const { mutate: uploadDocuments, isPending, isSuccess, reset } = useUploadDocuments()

  const [onboardingStep, setOnboardingStep] = useState<OnboardingStep>("check-email")
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentDocType, setCurrentDocType] = useState<string>("")
  const [hasUploadedInStep, setHasUploadedInStep] = useState(false)
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, string[]>>({})

  const goToNextStep = (next: OnboardingStep) => {
    setHasUploadedInStep(false)
    setOnboardingStep(next)
  }

  const openUploadModal = (docType: string) => {
    reset()
    setCurrentDocType(docType)
    setIsModalOpen(true)
  }

  const handleConfirmUpload = (filesToUpload: File[]) => {
    const deal = deals[0]
    if (!deal || !currentDocType) return

    for (const file of filesToUpload) {
      if (!WHITE_LISTED_FILE_TYPES.includes(file.type)) {
        toaster.create({
          title: `${file.type} is not a valid file type`,
          type: "error",
          closable: true,
          duration: 3000,
        })
        return
      }
    }

    const documentType = DOC_TYPE_TO_PREFIX[currentDocType]
    uploadDocuments(
      { documentType, dealIdStr: deal.id.toString(), files: filesToUpload },
      {
        onSuccess: () => {
          toaster.create({
            title: "Files uploaded successfully",
            type: "success",
            duration: 5000,
          })
          setUploadedFiles(prev => ({
            ...prev,
            [currentDocType]: [...(prev[currentDocType] ?? []), ...filesToUpload.map(f => f.name)]
          }))
          setIsModalOpen(false)
          setHasUploadedInStep(true)
        },
        onError: () => {
          toaster.create({
            title: "Upload failed. Please contact your equity partner.",
            type: "error",
            duration: 3000,
          })
        },
      }
    )
  }

  // Step 1: Check email for DocuSign
  if (onboardingStep === "check-email") {
    return (
      <Center w="100%" h="100%">
        <VStack position="relative" zIndex={1} w={{ base: "92%", md: "50%" }} p={{ base: 5, md: 8 }} borderRadius={10} bg="white" boxShadow="md" mt={20} gap={4}>
          <Text fontSize="xs" color="gray.400" alignSelf="flex-end">Step 1/3</Text>
          <Box borderRadius={5} bg="blue.300" p={3}>
            <FaCircleCheck size={30} color="white" />
          </Box>
          <Text fontSize="lg" fontWeight="bold" textAlign="center">
            Check Your Email
          </Text>
          <Text fontSize="sm" textAlign="center">
            Please check your email for a DocuSign NDA sent from{" "}
            <Text as="span" fontWeight="bold">docusign@esofund.com</Text>. There will be a link in the email that will take you to the DocuSign to sign the NDA. Please review and sign the form to proceed with your deal.
            Be sure to check your spam/junk folder if you don't see it in your inbox.
          </Text>
          <HStack w="100%" justifyContent="center">
            <Button variant="solid" onClick={() => setOnboardingStep("upload-equity")}>
              Continue
            </Button>
          </HStack>
        </VStack>
      </Center>
    )
  }

  // Step 2 Part 1: Upload Equity Holdings
  if (onboardingStep === "upload-equity") {
    return (
      <Center w="100%" h="100%">
        <VStack position="relative" zIndex={1} w={{ base: "92%", md: "50%" }} p={{ base: 5, md: 8 }} borderRadius={10} bg="white" boxShadow="md" mt={20} gap={4} align="stretch">
          <Text fontSize="xs" color="gray.400" alignSelf="flex-end">Step 2/3</Text>
          <Text fontSize="lg" fontWeight="bold">
            Tell Us About Your Equity
          </Text>
          <Text fontSize="sm" fontWeight="semibold">What we need from you:</Text>
          <HStack gap={0}>
            <Text fontSize="sm" fontWeight="bold">
              Equity Holdings (ISO, NSO, Shares, RSUs)
            </Text>
            <InfoTooltip>
              <Text fontSize="xs" mb={1}>Options (ISOs, NSOs): vested amounts, grant dates, strike prices.</Text>
              <Text fontSize="xs" mb={1}>Shares: stock certificate or vesting confirmation.</Text>
              <Text fontSize="xs">RSUs: vested amounts, grant dates, expiration date.</Text>
            </InfoTooltip>
          </HStack>
          <Box pl={4}>
            <Text fontSize="sm">- Options: screenshots or grant documents indicating the <Text as="span" fontWeight="bold">vested amounts, grant dates, and strike prices</Text></Text>
            <Text fontSize="sm">- Shares: screenshot of your stock certificate(s)</Text>
            <Text fontSize="sm">- RSUs: screenshots or grant documents of the <Text as="span" fontWeight="bold">vested amounts, grant dates, and expiration dates</Text></Text>
          </Box>
          {uploadedFiles["Equity Holdings (ISO, NSO, Shares, RSUs)"]?.length > 0 && (
            <Box bg="green.50" border="1px solid" borderColor="green.200" borderRadius="md" px={4} py={3}>
              <Text fontSize="xs" fontWeight="semibold" color="green.700" mb={1}>Uploaded files:</Text>
              {uploadedFiles["Equity Holdings (ISO, NSO, Shares, RSUs)"].map((name, i) => (
                <HStack key={i} gap={2}>
                  <FaFile size={10} color="#38A169" />
                  <Text fontSize="xs" color="green.700">{name}</Text>
                </HStack>
              ))}
            </Box>
          )}
          <HStack justifyContent="flex-end" gap={3} pt={2}>
            <Button variant={hasUploadedInStep ? "solid" : "outline"} onClick={() => goToNextStep("upload-fmv")}>
              {hasUploadedInStep ? "Next" : "I'll Do This Later"}
            </Button>
            <Button variant={hasUploadedInStep ? "outline" : "solid"} onClick={() => openUploadModal("Equity Holdings (ISO, NSO, Shares, RSUs)")}>
              {hasUploadedInStep ? "Upload More" : "Upload"}
            </Button>
          </HStack>
        </VStack>
        <UploadModal
          isOpen={isModalOpen}
          onOpenChange={setIsModalOpen}
          title="Upload Equity Holdings (ISO, NSO, Shares, RSUs)"
          uploadDocuments={handleConfirmUpload}
          isLoading={isPending}
          isSuccess={isSuccess}
        />
        <Toaster />
      </Center>
    )
  }

  // Step 2 Part 2: Upload FMV
  if (onboardingStep === "upload-fmv") {
    return (
      <Center w="100%" h="100%">
        <VStack position="relative" zIndex={1} w={{ base: "92%", md: "50%" }} p={{ base: 5, md: 8 }} borderRadius={10} bg="white" boxShadow="md" mt={20} gap={4} align="stretch">
          <Text fontSize="xs" color="gray.400" alignSelf="flex-end">Step 3/3</Text>
          <Text fontSize="lg" fontWeight="bold">
            One Last Thing: Your 409A Value
          </Text>
          <Text fontSize="sm" fontWeight="semibold">What we need from you:</Text>
          <HStack gap={0}>
            <Text fontSize="sm" fontWeight="bold">
              FMV (409A) Confirmation
            </Text>
            <InfoTooltip>
              <Text fontSize="xs" mb={1}>Find it in your equity portal:</Text>
              <Box as="ul" pl={4} fontSize="xs" mb={1}>
                <li>Carta — Portfolio page or bottom of the Exercise Simulator page</li>
                <li>Shareworks — Dashboard</li>
                <li>Other portals — typically on a summary page</li>
              </Box>
              <Text fontSize="xs">If capturing a screenshot, make sure your computer's date and time are visible in the image. If you can't locate the FMV, ask your stock plan administrator or CFO for the current 409A value.</Text>
            </InfoTooltip>
          </HStack>
          <Box pl={4}>
            <Text fontSize="sm">- Current 409A share price, find it in your equity portal or ask your stock plan administrator.</Text>
            <Text fontSize="sm">- If capturing screenshots of the FMV, be sure to capture your computer's time and date in the image.</Text>
          </Box>
          {uploadedFiles["FMV (409A) Confirmation"]?.length > 0 && (
            <Box bg="green.50" border="1px solid" borderColor="green.200" borderRadius="md" px={4} py={3}>
              <Text fontSize="xs" fontWeight="semibold" color="green.700" mb={1}>Uploaded files:</Text>
              {uploadedFiles["FMV (409A) Confirmation"].map((name, i) => (
                <HStack key={i} gap={2}>
                  <FaFile size={10} color="#38A169" />
                  <Text fontSize="xs" color="green.700">{name}</Text>
                </HStack>
              ))}
            </Box>
          )}
          <HStack justifyContent="flex-end" gap={3} pt={2}>
            <Button variant={hasUploadedInStep ? "solid" : "outline"} onClick={() => goToNextStep("thank-you")}>
              {hasUploadedInStep ? "Next" : "I'll Do This Later"}
            </Button>
            <Button variant={hasUploadedInStep ? "outline" : "solid"} onClick={() => openUploadModal("FMV (409A) Confirmation")}>
              {hasUploadedInStep ? "Upload More" : "Upload"}
            </Button>
          </HStack>
        </VStack>
        <UploadModal
          isOpen={isModalOpen}
          onOpenChange={setIsModalOpen}
          title="Upload FMV (409A) Confirmation"
          uploadDocuments={handleConfirmUpload}
          isLoading={isPending}
          isSuccess={isSuccess}
        />
        <Toaster />
      </Center>
    )
  }

  // Step 3: Thank you
  return (
    <Center w="100%" h="100%">
      <VStack position="relative" zIndex={1} w={{ base: "92%", md: "50%" }} p={{ base: 5, md: 8 }} borderRadius={10} bg="white" boxShadow="md" mt={20} gap={4}>
        <Box borderRadius={5} bg="green.300" p={3}>
          <FaCircleCheck size={30} color="white" />
        </Box>
        <Text fontSize="lg" fontWeight="bold" textAlign="center">
          Thank You!
        </Text>
        <Text fontSize="sm" textAlign="center">
          We'll be in contact shortly. In the meantime if you have any questions feel free to email or schedule a call with your equity partner!
        </Text>
        <Button variant="solid" onClick={() => navigate(viewedClient ? `/clients/${viewedClient.id}` : "/documents")}>
          Proceed to Portal
        </Button>
      </VStack>
    </Center>
  )
}
